// Adapted from davinci-elections apps/web/src/api/client.ts:1-120 @ 6e23f58
import {
  ApiErrorBodySchema,
  buildPath,
  routes,
  type ApiErrorCode,
  type RouteBody,
  type RouteDef,
  type RouteName,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
} from '@ipc/shared'

/**
 * Typed fetch client over the shared route table (`@ipc/shared` routes.ts).
 *
 *   const { items } = await api('places', { query: { q: 'Val' } })
 *   await api('registrationStart', { body: { provider: 'didit', name, email, acceptPrivacy: true } })
 *
 * - Same-origin requests with cookies (the registration and member sessions are httpOnly cookies).
 * - Non-2xx throws ApiError { status, code, message, details } parsed from the shared ApiErrorBody.
 * - 2xx bodies are parsed with the route's response schema (defaults applied, contract drift surfaced).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const isApiError = (err: unknown, code?: ApiErrorCode): err is ApiError =>
  err instanceof ApiError && (code === undefined || err.code === code)

type IsNever<T> = [T] extends [never] ? true : false

export type ApiCallOptions<N extends RouteName> = (IsNever<RouteParams<N>> extends true
  ? { params?: undefined }
  : { params: RouteParams<N> }) &
  (IsNever<RouteBody<N>> extends true ? { body?: undefined } : { body: RouteBody<N> }) &
  (IsNever<RouteQuery<N>> extends true ? { query?: undefined } : { query?: RouteQuery<N> }) & {
    headers?: Record<string, string>
    signal?: AbortSignal
  }

type OptionalIfEmpty<N extends RouteName> =
  IsNever<RouteParams<N>> extends true
    ? IsNever<RouteBody<N>> extends true
      ? [options?: ApiCallOptions<N>]
      : [options: ApiCallOptions<N>]
    : [options: ApiCallOptions<N>]

export function toQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return ''
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, Array.isArray(value) ? value.join(',') : String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

interface RawOptions {
  params?: Record<string, string | number>
  query?: Record<string, unknown>
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
}

/** The transport behind `api()`. */
export async function request<R extends RouteDef>(def: R, label: string, opts: RawOptions): Promise<unknown> {
  const url = buildPath(def.path, opts.params ?? {}) + toQueryString(opts.query)
  const hasBody = opts.body !== undefined
  const res = await fetch(url, {
    method: def.method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...opts.headers,
    },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  return parseResponse(res, def, label)
}

/** Reads a response with the shared contract: ApiError for a failure, the route's schema for a success. */
async function parseResponse<R extends RouteDef>(res: Response, def: R, label: string): Promise<unknown> {
  const text = await res.text()
  let json: unknown = undefined
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      json = undefined
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorBodySchema.safeParse(json)
    if (parsed.success) {
      const { code, message, details } = parsed.data.error
      throw new ApiError(res.status, code, message, details)
    }
    throw new ApiError(res.status, res.status === 404 ? 'not_found' : 'http_error', res.statusText || 'Request failed')
  }

  const parsed = def.response.safeParse(json)
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      console.error(`[api] ${label}: response does not match the shared contract`, parsed.error.issues, json)
      throw new ApiError(
        res.status,
        'contract_mismatch',
        `Unexpected response from ${def.method} ${def.path}`,
        parsed.error.issues
      )
    }
    // Outside dev the page carries on with the unvalidated body; leave a trace of which fields disagreed (paths only).
    console.warn(
      '[api] contract mismatch',
      label,
      parsed.error.issues.map((i) => i.path.join('.'))
    )
    return json
  }
  return parsed.data
}

/** A multipart upload (the own-capture frames): the body is a FormData, the browser sets the boundary. */
export async function requestForm<R extends RouteDef>(def: R, label: string, form: FormData): Promise<unknown> {
  const res = await fetch(def.path, {
    method: def.method,
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    body: form,
  })
  return parseResponse(res, def, label)
}

export async function api<N extends RouteName>(name: N, ...[options]: OptionalIfEmpty<N>): Promise<RouteResponse<N>> {
  const opts = (options ?? {}) as RawOptions
  return (await request(routes[name], name, opts)) as RouteResponse<N>
}
